import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Toaster, toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/contexts/AuthContext";

const STORAGE_KEY = "boscage_vip_popup_dismissed";

export default function VipPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    const handler = () => {
      if (localStorage.getItem(STORAGE_KEY)) return;
      setOpen(true);
    };
    window.addEventListener("boscage:vip-trigger", handler);
    return () => window.removeEventListener("boscage:vip-trigger", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/vip/subscribe", { email, name });
      setSubmitted(data);
      localStorage.setItem(STORAGE_KEY, "1");
      toast.success("Welcome to the Maison.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        data-testid="vip-popup"
        className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4 fade-in"
      >
        <div className="relative w-full max-w-2xl bg-[#f5f5f5] text-[#000000] grid md:grid-cols-5 overflow-hidden">
          <button
            data-testid="vip-popup-close"
            onClick={dismiss}
            aria-label="Close"
            className="absolute top-4 right-4 text-[#000000]/70 hover:text-[#000000] z-10"
          >
            <X size={20} />
          </button>

          <div className="hidden md:block md:col-span-2 relative">
            <img
              alt=""
              src="https://images.unsplash.com/photo-1489286696299-aa7486820bd5"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#111111] via-[#111111]/40 to-transparent" />
          </div>

          <div className="md:col-span-3 p-8 md:p-10">
            <p className="overline">Maison invitation</p>
            <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">
              Join the Boscage VIP
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-[#000000]/70">
              Trade-in access · private previews · 14-day pre-order tailoring · 10% off your first piece.
            </p>

            {submitted ? (
              <div
                data-testid="vip-success"
                className="mt-8 border border-[#000000]/40 p-5"
              >
                <p className="overline text-[#000000]">VIP Ticket Issued</p>
                <p className="font-serif text-xl mt-2">{submitted.vip_ticket}</p>
                <p className="mt-2 text-xs text-[#000000]/60">
                  Keep this code. Apply at checkout or in the Trade-In portal.
                </p>
                <button
                  data-testid="vip-popup-done"
                  onClick={dismiss}
                  className="btn-primary mt-6"
                >
                  Enter the Maison
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-4">
                <input
                  data-testid="vip-name-input"
                  className="input-light bg-[#ffffff]"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
                <input
                  data-testid="vip-email-input"
                  type="email"
                  className="input-light bg-[#ffffff]"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button
                  type="submit"
                  data-testid="vip-submit-button"
                  disabled={submitting}
                  className="btn-vip w-full"
                >
                  <span>{submitting ? "Submitting…" : "Claim VIP ticket"}</span>
                </button>
                <p className="text-[10px] text-[#000000]/40 tracking-wider uppercase">
                  Free. No spam. Unsubscribe any time.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
      <Toaster theme="dark" position="top-center" />
    </>
  );
}
